/**
 * Slug filter strategies for Next.js page generation
 *
 * This module contains the filtering logic for determining which pages should be prerendered
 * based on platform, page types, forced slugs, and broken slugs.
 */

import { filterEmptyStrings } from '../array/filterEmptyStrings';

export const PLATFORM_NEXTJS = 'next.js';

/**
 * Page slug skeleton node interface
 * Represents page data from Contentful for filtering and route metadata
 */
export interface PageSlugNode {
  slug: string;
  platform?: string | null;
  pageType?: string | null;
  pageSubType?: string | null;
  pageAccess?: string | null;
  pageName?: string | null;
}

/**
 * Environment variables for slug filtering
 */
export interface SlugFilterEnvironment {
  brokenSlugs: string[];
  forcedSlugs: string[];
  preview: boolean;
  forcedPageTypes: string[];
  renderingMode: string;
}

/**
 * Strategy interface for filtering slugs based on different criteria.
 */
export interface SlugFilterStrategy {
  getFilteredPages(): PageSlugNode[];
}

/**
 * Reads and processes environment variables related to page slug filtering and rendering mode.
 * This function handles the following environment variables:
 * - `CONTENTFUL_BROKEN_SLUGS`: A comma-separated list of slugs to exclude from results.
 * - `CONTENTFUL_FORCED_SLUGS`: A comma-separated list of slugs to include in results (used for local dev).
 * - `CONTENTFUL_PREVIEW_MODE`: A boolean flag ('true' or 'false') to determine if preview content should be fetched.
 * - `CONTENTFUL_FORCED_PAGE_TYPES`: A comma-separated list of page types to forcefully include (used for migration).
 * - `RENDERING_MODE`: A string ('static' or 'dynamic') to determine if pages should be built or not.
 * @returns An object containing the processed environment variables
 */
export function readSlugFilterEnvironment(): SlugFilterEnvironment {
  // Filter out empty strings to handle cases where env vars are set to ''
  const brokenSlugs = process.env['CONTENTFUL_BROKEN_SLUGS']
    ? filterEmptyStrings(process.env['CONTENTFUL_BROKEN_SLUGS'].split(','))
    : [];
  const forcedSlugs = process.env['CONTENTFUL_FORCED_SLUGS']
    ? filterEmptyStrings(process.env['CONTENTFUL_FORCED_SLUGS'].split(','))
    : [];
  const preview = process.env['CONTENTFUL_PREVIEW_MODE'] === 'true';
  const forcedPageTypes = process.env['CONTENTFUL_FORCED_PAGE_TYPES']
    ? filterEmptyStrings(process.env['CONTENTFUL_FORCED_PAGE_TYPES'].split(','))
    : [];
  const renderingMode = process.env['RENDERING_MODE'] || 'static';

  return { brokenSlugs, forcedSlugs, preview, forcedPageTypes, renderingMode };
}

/**
 * Union-based filter strategy that combines page type, platform, and forced slug filtering.
 *
 * This implements the union approach for gradual Gatsby to Next.js migration:
 * - Step 1: Pages matching forcedPageTypes (ANY platform - if forcedPageTypes set)
 * - Step 2: ALL pages with platform='next.js' (UNION - ensures all Next.js pages are included)
 * - Step 3: Pages matching forcedSlugs (ANY platform - if forcedSlugs set)
 * - Uniqueness: Automatically guaranteed by Map (keyed by slug)
 * - Final filter: Exclude broken slugs
 *
 * The result is a UNION of all three collections. This allows specific pages to be built
 * on both Gatsby and Next.js during the migration period by using forced page types or slugs.
 */
export class UnionSlugFilterStrategy implements SlugFilterStrategy {
  private candidatePages: Map<string, PageSlugNode> = new Map();

  constructor(
    private pages: PageSlugNode[],
    private forcedPageTypes: string[],
    private forcedSlugs: string[],
    private brokenSlugs: string[],
  ) {
    this.buildCandidatePagesMap();
  }

  /**
   * Checks if a page matches the forced page types filter.
   * A page matches if it has a pageType that is in the forcedPageTypes list.
   * Platform is NOT checked - this allows non-Next.js pages to be included during migration.
   *
   * @param page - The page to check
   * @returns True if the page matches the page type filter
   */
  private matchesPageTypeFilter(page: PageSlugNode): boolean {
    return !!page.pageType && this.forcedPageTypes.includes(page.pageType);
  }

  /**
   * Checks if a page has platform='next.js'.
   * This is the core filter that ensures only Next.js pages are built.
   *
   * @param page - The page to check
   * @returns True if the page has platform='next.js'
   */
  private matchesPlatformFilter(page: PageSlugNode): boolean {
    return page.platform === PLATFORM_NEXTJS;
  }

  /**
   * Checks if a page slug is in the forced slugs list.
   * A page matches if its slug is in the forcedSlugs list.
   * Platform is NOT checked - this allows non-Next.js pages to be included during migration.
   *
   * @param page - The page to check
   * @returns True if the page slug matches the forced slugs filter
   */
  private matchesForcedSlugFilter(page: PageSlugNode) {
    return this.forcedSlugs.includes(page.slug);
  }

  /**
   * Builds the candidate pages map by applying union-based filtering.
   * Executes three filter steps in sequence:
   * 1. Pages matching forcedPageTypes (ANY platform - if forcedPageTypes set)
   * 2. ALL pages with platform='next.js' (always applied)
   * 3. Pages matching forcedSlugs (ANY platform - if forcedSlugs set)
   *
   * Steps 1 and 3 intentionally do NOT filter by platform to support gradual migration.
   * Results are stored in candidatePages Map for deduplication and fast lookup.
   */
  private buildCandidatePagesMap(): void {
    const filterSteps = [
      // Step 1: Add pages matching page type filter AND platform='next.js'
      {
        enabled: this.forcedPageTypes.length > 0,
        matcher: (page: PageSlugNode) => this.matchesPageTypeFilter(page),
      },
      // Step 2: Add ALL pages with platform='next.js' (UNION approach)
      {
        enabled: true,
        matcher: (page: PageSlugNode) => this.matchesPlatformFilter(page),
      },
      // Step 3: Add pages from forced slugs AND platform='next.js'
      {
        enabled: this.forcedSlugs.length > 0,
        matcher: (page: PageSlugNode) => this.matchesForcedSlugFilter(page),
      },
    ];

    filterSteps.forEach(({ enabled, matcher }) => {
      if (enabled) {
        this.pages
          .filter((page: PageSlugNode) => matcher(page))
          .forEach(page => this.candidatePages.set(page.slug, page));
      }
    });
  }

  /**
   * Returns the filtered pages, excluding broken slugs.
   * This is the final output of the strategy, ready to be normalized.
   *
   * @returns Array of filtered page nodes, excluding broken slugs
   */
  getFilteredPages(): PageSlugNode[] {
    return Array.from(this.candidatePages.values()).filter(page => !this.brokenSlugs.includes(page.slug));
  }
}

/**
 * Apply slug filtering based on environment variables and page data.
 * This is the main entry point for slug filtering logic.
 *
 * @param pages - Array of page nodes to filter
 * @param env - Environment configuration (optional, reads from process.env if not provided)
 * @returns Filtered array of page nodes
 */
export function applySlugFiltering(pages: PageSlugNode[], env?: SlugFilterEnvironment): PageSlugNode[] {
  const environment = env || readSlugFilterEnvironment();
  const { brokenSlugs, forcedSlugs, forcedPageTypes, renderingMode } = environment;

  // Dynamic mode returns empty array
  if (renderingMode === 'dynamic') {
    return [];
  }

  // If no page type filtering is needed and forced slugs are set, return forced slugs directly
  if (forcedPageTypes.length === 0 && forcedSlugs.length > 0) {
    return pages.filter(page => forcedSlugs.includes(page.slug) && !brokenSlugs.includes(page.slug));
  }

  // Otherwise use union-based filtering strategy
  const strategy = new UnionSlugFilterStrategy(pages, forcedPageTypes, forcedSlugs, brokenSlugs);

  return strategy.getFilteredPages();
}
